<template>
  <section>
    <loader v-if="isLoading"></loader>
    <div v-if="userCreditCards && userCreditCards.length > 0">
      <div v-for="userCreditCard in userCreditCards">
        <div class="card pointer-item" @click="selectUserCreditCard(userCreditCard)">
          <div class="card-body py-2">
            <div class="d-flex justify-content-start align-items-center text-small">
              <div>
                <img src="/static/img/brand-visa.svg"
                     height="40"
                     alt="visa"
                     v-if="userCreditCard.payment_data.brand === 'visa'">
                <img src="/static/img/brand-master-card.svg"
                     height="36"
                     alt="master card"
                     v-else-if="userCreditCard.payment_data.brand === 'mastercard'">
                <img src="/static/img/brand-jcb.svg"
                     height="36"
                     alt="jcb"
                     v-else>
              </div>
              <div class="flex-fill pl-3">
                <div :class="[userCreditCard.payment_data.is_expired ? '-status-offline' : 'text-secondary']">
                  <span v-if="userCreditCard.payment_data.bank">{{ userCreditCard.payment_data.bank }}</span>
                  <span v-else>{{ $t('message.payment_method.card_no_bank') }}</span>
                </div>
                <div :class="[userCreditCard.payment_data.is_expired ? '-status-offline' : 'text-secondary']">
                  *{{ userCreditCard.payment_data.last_digits }} ({{ userCreditCard.payment_data.expire_month }}/{{ userCreditCard.payment_data.expire_year }})
                  <b class="-status-alert" v-if="userCreditCard.payment_data.is_expired">&nbsp;{{
                    $t('message.payment_method.expired') }}</b>
                </div>
              </div>
              <div :class="checkConditions(userCreditCard)">
                <span v-if="userCreditCard.is_default">
                  <i class="fa fa-check text-success"></i>
                </span>
                <span v-else>&nbsp;</span>
              </div>
              <div class="text-secondary" v-if="canEditCard">
                <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none" no-caret>
                  <template #button-content>
                    <i class="fas fa-ellipsis-h text-secondary"></i>
                  </template>
                  <b-dropdown-item href="#"
                                   v-if="!userCreditCard.is_default && !userCreditCard.payment_data.is_expired"
                                   @click="displaySetDefaultUserCreditCard(userCreditCard, 1)">
                    {{ $t('message.payment_method.set_default_card') }}
                  </b-dropdown-item>
                  <b-dropdown-item href="#"
                                   v-if="userCreditCard.is_default"
                                   @click="displaySetDefaultUserCreditCard(userCreditCard, 0)">
                    {{ $t('message.payment_method.cancel_default_card') }}
                  </b-dropdown-item>
                  <b-dropdown-divider
                    v-if="!userCreditCard.is_default && !userCreditCard.payment_data.is_expired"></b-dropdown-divider>
                  <b-dropdown-item href="#"
                                   @click="displayDeleteCardDialog(userCreditCard)">
                    <span class="-status-alert">{{ $t('message.payment_method.delete_card') }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <button class="btn btn-outline-info btn-block" @click="openPaymentModal()">
        <i class="fas fa-plus"></i> {{ $t('message.payment_method.add_new_card') }}
      </button>
    </div>
    <payment-form-modal ref="paymentFormModal" :action="action" />
  </section>
</template>

<script>
  import axios from 'axios'
  import Loader from '../../loader/Loader'
  import PaymentFormModal from './UserPaymentForm'
  import { mapState } from 'vuex'

  export default {
    props: {
      canEditCard: Boolean,
      action: String
    },
    components: {
      Loader,
      PaymentFormModal
    },
    data () {
      return {
        isLoading: false,
        userCreditCards: null
      }
    },
    computed: {
      ...mapState('booking', ['preCheckoutData']),
      ...mapState('payment', ['isSaving'])
    },
    watch: {
      isSaving () {
        if (!this.isSaving) {
          this.fetchUserCreditCards(true)
        }
      }
    },
    methods: {
      selectUserCreditCard (userCreditCard) {
        if (this.canEditCard === false) {
          if (!userCreditCard.payment_data.is_expired) {
            for (let creditCard of this.userCreditCards) {
              creditCard.is_default = 0
            }
            userCreditCard.is_default = 1
            this.$emit('onSelected', userCreditCard)
          }
        }
      },
      checkConditions (userCreditCard) {
        if (userCreditCard.is_default && this.canEditCard) {
          return 'px-lg-5 px-5'
        } else {
          return 'text-right'
        }
      },
      openPaymentModal () {
        this.$emit('onAddNewCard', true)
        this.$refs.paymentFormModal.showPaymentModal(true)
      },
      async fetchUserCreditCards (resetData) {
        if (resetData) {
          this.isLoading = true
          this.userCreditCards = null
        }
        const url = '/payments/user-credit-cards'
        const {data} = await axios.get(url)
        this.isLoading = false
        if (!data.error) {
          this.userCreditCards = data.user_credit_cards
          if (this.userCreditCards) {
            for (const userCreditCard of this.userCreditCards) {
              if (userCreditCard.is_default) {
                this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'payment_method', value: 'credit_card'})
                this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'omise_customer_id', value: userCreditCard.omise_customer_id})
                this.selectUserCreditCard(userCreditCard)
                break
              }
            }
          }
        }
      },
      async setDefaultUserCreditCard (userCreditCard, isDefault) {
        const url = `payments/user-credit-cards/${userCreditCard.id}/set-default`
        const submitData = {is_default: isDefault}
        const {data} = await axios.post(url, submitData)
        if (!data.error) {
          await this.fetchUserCreditCards(false)
        }
      },
      async deleteUserCreditCard (userCreditCard) {
        const url = `/payments/user-credit-cards/${userCreditCard.id}`
        const {data} = await axios.delete(url)
        if (!data.error) {
          this.$swal({text: this.$i18n.t('message.success'), type: 'success'})
          await this.fetchUserCreditCards(false)
        }
      },
      displaySetDefaultUserCreditCard (userCreditCard, isDefault) {
        const text = (isDefault) ? this.$i18n.t('message.confirm.set_default_card') : this.$i18n.t('message.confirm.cancel_default_card')
        this.$swal({
          title: this.$i18n.t('message.confirm.title'),
          text: text,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          this.setDefaultUserCreditCard(userCreditCard, isDefault)
        })
      },
      displayDeleteCardDialog (userCreditCard) {
        this.$swal({
          title: this.$i18n.t('message.confirm.title'),
          text: this.$i18n.t('message.confirm.remove_payment'),
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          this.deleteUserCreditCard(userCreditCard)
        })
      }
    },
    mounted () {
      this.fetchUserCreditCards(true)
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/user/payment/UserCreditCards.vue